import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Hls from "hls.js";
import { api, BACKEND_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Star, StarOff, Search, RefreshCw, ChevronLeft, ChevronRight, Radio, Maximize2, Play, Pause, Minimize2, X } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1584527810790-01a904be796b?crop=entropy&cs=srgb&fm=jpg&q=70&w=800";
const MAX_FAVORITES = 15;
const PAGE_SIZE = 24;
// How long a stalled HLS player waits before tearing down and retrying.
const HLS_STALL_AFTER_MS = 12000;
const HLS_RETRY_AFTER_MS = 4000;
// Wall cycle interval (one favorite at a time).
const CYCLE_INTERVAL_MS = 10000;

// Server-proxied snapshot URL — bypasses cross-origin / referer blocks on agency feeds.
const snapshotUrl = (camId, t) => `${BACKEND_URL}/api/cameras/snapshot/${encodeURIComponent(camId)}?t=${t}`;
// Server-proxied HLS playlist — upstream advertises invalid CORS headers that
// browsers silently reject. Our backend re-emits clean CORS + Referer.
const hlsUrl = (camId) => `${BACKEND_URL}/api/cameras/hls/${encodeURIComponent(camId)}/playlist.m3u8`;

// Live HLS player that auto-reconnects on fatal errors or stalls. Reports a
// `phase` to its parent ("loading" | "playing" | "retrying" | "dead") so the
// card can render the right overlay.
function LiveVideo({ cam, onPhase, onQuality, fit = "cover" }) {
  const ref = useRef(null);
  const [attempt, setAttempt] = useState(0);
  // Bump `attempt` to force a complete teardown + remount of the player.
  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  // Hold the latest callbacks in refs so the main effect only re-runs when
  // the actual stream identity changes. Otherwise inline parent callbacks
  // (identity-fresh on every render) + React.StrictMode would tear the HLS
  // instance down repeatedly, aborting every playlist XHR (`ERR_ABORTED`).
  const onPhaseRef = useRef(onPhase);
  const onQualityRef = useRef(onQuality);
  useEffect(() => { onPhaseRef.current = onPhase; }, [onPhase]);
  useEffect(() => { onQualityRef.current = onQuality; }, [onQuality]);
  useEffect(() => {
    const video = ref.current;
    if (!video || !cam.video_url) return undefined;
    const src = hlsUrl(cam.id);
    let hls;
    let cancelled = false;
    let stallTimer;
    let retryTimer;

    const reportPhase = (p) => { if (!cancelled) onPhaseRef.current?.(p); };
    reportPhase("loading");

    // If neither MANIFEST_PARSED nor a frame arrives within HLS_STALL_AFTER_MS,
    // tear down and retry. This catches "playlist 200 but no segments" scenarios.
    const startStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        if (cancelled) return;
        reportPhase("retrying");
        retryTimer = setTimeout(() => { if (!cancelled) retry(); }, HLS_RETRY_AFTER_MS);
      }, HLS_STALL_AFTER_MS);
    };

    const onPlaying = () => { reportPhase("playing"); clearTimeout(stallTimer); };
    const onWaiting = () => { startStallTimer(); };
    const onStalled = () => { startStallTimer(); };
    const onError = () => {
      reportPhase("retrying");
      clearTimeout(stallTimer);
      retryTimer = setTimeout(() => { if (!cancelled) retry(); }, HLS_RETRY_AFTER_MS);
    };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("stalled", onStalled);
    video.addEventListener("error", onError);

    startStallTimer();

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.play().catch(() => { /* autoplay throttled */ });
    } else if (Hls.isSupported()) {
      hls = new Hls({
        liveDurationInfinity: true,
        lowLatencyMode: true,
        backBufferLength: 8,
        maxBufferLength: 20,
        // Force the highest bitrate variant right away. `capLevelToPlayerSize`
        // is intentionally OFF — with it on, HLS filtered out 720p/1080p
        // variants when the tile was smaller than the source, so HD cams
        // (e.g. nysdot-5194 @ 1280x720) played downscaled at 240p even though
        // the upstream offered HD. Bandwidth trade-off is worth the clarity.
        startLevel: -1,
        capLevelToPlayerSize: false,
        autoStartLoad: true,
        manifestLoadingMaxRetry: 3,
        levelLoadingMaxRetry: 3,
        fragLoadingMaxRetry: 4,
        // The HLS proxy is public and responds with `Access-Control-Allow-Origin: *`.
        // Browsers reject `*` when `withCredentials=true`, silently killing the
        // stream and leaving only the poster snapshot visible. Keep this off.
        xhrSetup: (xhr) => { xhr.withCredentials = false; },
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // Jump straight to the highest available quality level once manifest
        // is parsed so fullscreen isn't waiting for ABR to ratchet up.
        try {
          const levels = hls.levels || [];
          if (levels.length > 0) hls.nextLevel = levels.length - 1;
          // Report the highest resolution so the card can render an HD/4K badge.
          const maxH = levels.reduce((m, l) => Math.max(m, l.height || 0), 0);
          if (maxH > 0 && !cancelled) onQualityRef.current?.({ height: maxH, levels: levels.length });
        } catch { /* ignore */ }
        video.play().catch(() => { /* autoplay blocked is fine */ });
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data?.fatal) return;
        // Try in-place recovery once before scheduling a full remount.
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          try { hls.startLoad(); return; } catch { /* fallthrough */ }
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          try { hls.recoverMediaError(); return; } catch { /* fallthrough */ }
        }
        onError();
      });
    } else {
      reportPhase("dead");
    }

    return () => {
      cancelled = true;
      clearTimeout(stallTimer);
      clearTimeout(retryTimer);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("stalled", onStalled);
      video.removeEventListener("error", onError);
      if (hls) { try { hls.destroy(); } catch { /* ignore */ } }
      try { video.pause(); video.removeAttribute("src"); video.load(); } catch { /* ignore */ }
    };
  }, [cam.id, cam.video_url, attempt, retry]);

  return (
    <video
      ref={ref}
      data-testid={`camera-video-${cam.id}`}
      className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"}`}
      autoPlay
      muted
      playsInline
      controls={false}
      poster={cam.snapshot_alive === false ? undefined : snapshotUrl(cam.id, 0)}
    />
  );
}

function CameraCard({ cam, favored, onToggle, sizeMode = "grid", parentFullscreen = false }) {
  const [tick, setTick] = useState(() => Date.now());
  const [errored, setErrored] = useState(false);
  // "loading" | "playing" | "retrying" | "dead" (HLS-only)
  const [videoPhase, setVideoPhase] = useState("loading");
  // Highest available HLS variant height, e.g. 720, 1080, 2160
  const [maxHeight, setMaxHeight] = useState(0);
  const hasLive = !!cam.video_url && videoPhase !== "dead";
  const cardRef = useRef(null);
  const [selfFs, setSelfFs] = useState(false);
  const isFs = selfFs || parentFullscreen;

  useEffect(() => {
    const onFsChange = () => setSelfFs(document.fullscreenElement === cardRef.current);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Non-HLS cameras refresh by polling the snapshot every 10 s.
  useEffect(() => {
    if (hasLive) return undefined;
    const t = setInterval(() => { setTick(Date.now()); setErrored(false); }, 10000);
    return () => clearInterval(t);
  }, [cam.id, hasLive]);

  const refresh = () => {
    setTick(Date.now());
    setErrored(false);
    setVideoPhase("loading"); // remount cycle on parent will reattempt HLS too
  };

  const toggleFullscreen = async () => {
    const el = cardRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen({ navigationUI: "hide" });
      }
    } catch { /* user gesture missing or unsupported — ignore */ }
  };

  return (
    <Card ref={cardRef} data-testid={`camera-card-${cam.id}`} className="overflow-hidden h-full flex flex-col bg-black">
      <div className="relative bg-black flex-1 aspect-video">
        {hasLive ? (
          <LiveVideo
            cam={cam}
            onPhase={setVideoPhase}
            onQuality={(q) => setMaxHeight(q.height || 0)}
            fit={isFs ? "contain" : "cover"}
          />
        ) : (
          <img
            key={tick}
            src={errored ? PLACEHOLDER_IMG : snapshotUrl(cam.id, tick)}
            onError={() => setErrored(true)}
            alt={cam.name}
            className={`w-full h-full ${isFs ? "object-contain" : "object-cover"}`}
            referrerPolicy="no-referrer"
            loading="lazy"
          />
        )}
        <div className="absolute top-2 left-2 font-mono text-[10px] bg-black/65 text-white px-2 py-0.5 rounded-sm">
          {cam.region || cam.city} • {cam.highway}
        </div>
        {hasLive && videoPhase === "playing" && (
          <div
            data-testid={`live-indicator-${cam.id}`}
            className="absolute top-2 left-1/2 -translate-x-1/2 font-mono text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-sm flex items-center gap-1 shadow-lg"
            title="Live video stream"
          >
            <Radio className="w-3 h-3 animate-pulse" /> LIVE
          </div>
        )}
        {hasLive && videoPhase === "playing" && maxHeight > 0 && (
          <div
            data-testid={`quality-indicator-${cam.id}`}
            className={`absolute top-8 left-1/2 -translate-x-1/2 font-mono text-[10px] font-bold px-2 py-0.5 rounded-sm shadow-lg tracking-wider ${
              maxHeight >= 2160
                ? "bg-fuchsia-500 text-white"
                : maxHeight >= 1080
                ? "bg-emerald-500 text-white"
                : maxHeight >= 720
                ? "bg-sky-500 text-white"
                : maxHeight >= 480
                ? "bg-slate-500 text-white"
                : "bg-slate-700 text-slate-200"
            }`}
            title={`Source resolution: ${maxHeight}p — this is the highest the upstream camera offers`}
          >
            {maxHeight >= 2160 ? "4K" : maxHeight >= 1080 ? "FHD" : maxHeight >= 720 ? "HD" : maxHeight >= 480 ? "SD" : `${maxHeight}p`}
          </div>
        )}
        {hasLive && videoPhase === "retrying" && (
          <div
            data-testid={`live-retrying-${cam.id}`}
            className="absolute top-2 left-1/2 -translate-x-1/2 font-mono text-[10px] bg-amber-500 text-black px-2 py-0.5 rounded-sm flex items-center gap-1 shadow-lg"
            title="Stream stalled — reconnecting"
          >
            <RefreshCw className="w-3 h-3 animate-spin" /> RECONNECTING
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <button
            data-testid={`fullscreen-${cam.id}`}
            onClick={toggleFullscreen}
            className="p-1.5 bg-black/65 hover:bg-black/85 rounded-sm text-white"
            title="Fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            data-testid={`favorite-toggle-${cam.id}`}
            onClick={() => onToggle(cam)}
            className="p-1.5 bg-black/65 hover:bg-black/85 rounded-sm text-white"
            title={favored ? "Unfavorite" : "Save favorite"}
          >
            {favored ? <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /> : <StarOff className="w-4 h-4" />}
          </button>
        </div>
        {errored && !hasLive && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-xs bg-black/55 font-mono">
            FEED OFFLINE
          </div>
        )}
      </div>
      {sizeMode === "grid" && (
        <CardContent className="p-2 flex items-center justify-between gap-2 shrink-0 bg-card">
          <div className="min-w-0">
            <div className="font-medium text-xs truncate">{cam.name}</div>
            <div className="text-[10px] text-muted-foreground truncate">
              {cam.region || cam.city}{cam.video_url ? " • live stream" : ""}
            </div>
          </div>
          <Button size="icon" variant="ghost" onClick={refresh} title="Refresh" className="h-7 w-7">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

// Fullscreen-aware cycle stage. Fullscreen is bound to the outer container so
// switching the currently-shown favorite (mid-cycle) doesn't drop us out of
// fullscreen — nav controls stay visible in fullscreen too.
function FullscreenCycleStage({ favorites, cycleIdx, onNext, onPrev, onStop, onToggleFav }) {
  const stageRef = useRef(null);
  const [isFs, setIsFs] = useState(false);

  useEffect(() => {
    const onFsChange = () => setIsFs(document.fullscreenElement === stageRef.current);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (stageRef.current) {
        await stageRef.current.requestFullscreen({ navigationUI: "hide" });
      }
    } catch { /* ignore */ }
  };

  // Keyboard: ← / → advance, F toggles fullscreen, Esc stops the cycle
  // (fullscreen exits on Esc automatically; we listen for keydown only when
  // fullscreen so we don't stomp on the rest of the app).
  useEffect(() => {
    if (!isFs) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") onNext();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key.toLowerCase() === "f") toggleFullscreen();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFs, onNext, onPrev]);

  const f = favorites[cycleIdx % favorites.length];
  if (!f) return null;
  const cam = {
    id: f.camera_id, name: f.name, snapshot_url: f.url,
    city: f.city, state: f.state, region: f.region || f.city,
    highway: f.highway || "★",
    video_url: f.video_url || "",
    snapshot_alive: f.snapshot_alive !== false,
    source: f.source,
  };

  return (
    <div
      ref={stageRef}
      data-testid="camera-cycle-stage"
      className={`h-full flex flex-col gap-2 ${isFs ? "bg-black p-3" : ""}`}
    >
      <div className="font-mono text-[11px] text-muted-foreground flex items-center justify-between shrink-0">
        <span data-testid="cycle-progress">
          Cycling {cycleIdx + 1} / {favorites.length} · changes every {CYCLE_INTERVAL_MS / 1000}s
        </span>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={onPrev} data-testid="cycle-prev-btn" title="Previous camera">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={onNext} data-testid="cycle-next-inline-btn" title="Next camera">
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={toggleFullscreen} data-testid="cycle-fullscreen-btn" title={isFs ? "Exit fullscreen" : "Fullscreen"}>
            {isFs ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>
          {isFs && (
            <Button size="icon" variant="outline" className="h-7 w-7" onClick={onStop} data-testid="cycle-stop-inline-btn" title="Stop cycle">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <CameraCard
          key={`cycle-${cam.id}`}
          cam={cam}
          favored
          onToggle={() => onToggleFav(cam)}
          sizeMode="wall"
          parentFullscreen={isFs}
        />
      </div>
    </div>
  );
}

function fitGrid(n) {
  if (n <= 1) return { cols: 1, rows: 1 };
  if (n <= 2) return { cols: 2, rows: 1 };
  if (n <= 4) return { cols: 2, rows: 2 };
  if (n <= 6) return { cols: 3, rows: 2 };
  if (n <= 9) return { cols: 3, rows: 3 };
  if (n <= 12) return { cols: 4, rows: 3 };
  return { cols: 5, rows: 3 };
}

export default function CamerasPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [regions, setRegions] = useState([]);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [region, setRegion] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cycling, setCycling] = useState(false);
  const [cycleIdx, setCycleIdx] = useState(0);
  // Whole-page fullscreen mode — hides app shell chrome (header, sidebar,
  // filters, tabs) so the entire viewport is cameras only.
  const pageRef = useRef(null);
  const [pageFs, setPageFs] = useState(false);
  useEffect(() => {
    const onFsChange = () => setPageFs(document.fullscreenElement === pageRef.current);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);
  const togglePageFs = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (pageRef.current) await pageRef.current.requestFullscreen({ navigationUI: "hide" });
    } catch { /* ignore */ }
  };

  // Advance the wall cycle on a timer when cycling is on.
  useEffect(() => {
    if (!cycling || favorites.length < 2) return undefined;
    const t = setInterval(() => {
      setCycleIdx((i) => (i + 1) % favorites.length);
    }, CYCLE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [cycling, favorites.length]);

  // Keep the cycle index sane if favorites list size changes.
  useEffect(() => {
    if (cycleIdx >= favorites.length) setCycleIdx(0);
  }, [favorites.length, cycleIdx]);

  const loadCameras = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (q) params.q = q;
      if (region) params.region = region;
      if (source) params.source = source;
      const { data } = await api.get("/cameras", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [q, region, source, page]);

  const loadFavorites = useCallback(async () => {
    const { data } = await api.get("/cameras/favorites");
    setFavorites(data);
  }, []);

  const loadRegions = useCallback(async () => {
    try {
      const { data } = await api.get("/cameras/regions");
      setRegions(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadCameras(); }, [loadCameras]);
  useEffect(() => { loadFavorites(); loadRegions(); }, [loadFavorites, loadRegions]);

  const favIds = useMemo(() => new Set(favorites.map((f) => f.camera_id)), [favorites]);

  const toggleFav = async (cam) => {
    if (favIds.has(cam.id)) {
      await api.delete(`/cameras/favorites/${cam.id}`);
      toast.success("Removed from favorites");
    } else {
      if (favorites.length >= MAX_FAVORITES) {
        toast.error(`Max ${MAX_FAVORITES} favorites — remove one first`);
        return;
      }
      await api.post("/cameras/favorites", {
        camera_id: cam.id, name: cam.name, url: cam.snapshot_url, city: cam.city, state: cam.state, group: "default",
      });
      toast.success("Saved to favorites");
    }
    loadFavorites();
  };

  const applySearch = () => { setPage(0); setQ(qInput.trim()); };

  const wallGrid = fitGrid(favorites.length);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div ref={pageRef} className={`absolute inset-0 flex flex-col ${pageFs ? "bg-black" : ""}`} data-testid="cameras-page">
      {!pageFs && (
      <div className="p-4 border-b border-border bg-card/80 backdrop-blur shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-xs text-primary tracking-widest">// TRAFFIC CAMERA CENTER</div>
            <h1 className="text-xl font-bold">Live Traffic Cameras — New York</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-muted-foreground font-mono">
              Catalogue {total.toLocaleString()} • Favorites {favorites.length}/{MAX_FAVORITES}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={togglePageFs}
              data-testid="page-fullscreen-btn"
              className="h-8"
              title="Show all cameras full-screen (Esc to exit)"
            >
              <Maximize2 className="w-3.5 h-3.5 mr-1.5" /> FULLSCREEN ALL
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="camera-search-input"
              placeholder="Search by roadway, city, intersection…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applySearch()}
              className="pl-10 font-mono h-9"
            />
          </div>
          <Select value={region || "__any__"} onValueChange={(v) => { setRegion(v === "__any__" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-48 h-9" data-testid="region-filter"><SelectValue placeholder="Borough / County" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">All Areas</SelectItem>
              {regions.map((r) => (
                <SelectItem key={r.name} value={r.name}>{r.name} ({r.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={source || "__any__"} onValueChange={(v) => { setSource(v === "__any__" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-36 h-9" data-testid="source-filter"><SelectValue placeholder="Network" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">All Networks</SelectItem>
              <SelectItem value="state">State Network</SelectItem>
              <SelectItem value="city">City Network</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={applySearch} data-testid="search-btn" className="h-9">Apply</Button>
        </div>
      </div>
      )}

      <div className="flex-1 overflow-hidden">
        <Tabs defaultValue="wall" className="h-full flex flex-col">
          {!pageFs && (
          <div className="px-4 pt-2 shrink-0 flex items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="wall" data-testid="tab-wall">My Wall ({favorites.length})</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all-cams">Library ({total.toLocaleString()})</TabsTrigger>
            </TabsList>
            {favorites.length > 1 && (
              <div className="flex items-center gap-2 font-mono text-[10px]">
                <Button
                  size="sm"
                  variant={cycling ? "default" : "outline"}
                  onClick={() => setCycling((v) => !v)}
                  data-testid="cycle-toggle-btn"
                  className="h-8"
                  title={cycling ? "Stop cycling" : "Cycle through favorites one by one"}
                >
                  {cycling ? <Pause className="w-3.5 h-3.5 mr-1.5" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                  {cycling ? "STOP CYCLE" : "CYCLE"}
                </Button>
                {cycling && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCycleIdx((i) => (i + 1) % favorites.length)}
                    data-testid="cycle-next-btn"
                    className="h-8"
                    title="Skip to next camera"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
          )}

          {pageFs && (
            <Button
              size="icon"
              variant="outline"
              onClick={togglePageFs}
              data-testid="page-fullscreen-exit-btn"
              className="fixed top-3 right-3 z-50 h-9 w-9 bg-black/70 border-white/30 text-white hover:bg-black/90"
              title="Exit fullscreen (Esc)"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
          )}

          <TabsContent value="wall" className="flex-1 overflow-hidden p-3">
            {favorites.length === 0 ? (
              <div className="h-full flex items-center justify-center text-muted-foreground font-mono text-sm">
                No cameras pinned yet. Switch to &ldquo;Library&rdquo; and ★ up to {MAX_FAVORITES} feeds.
              </div>
            ) : cycling ? (
              <FullscreenCycleStage
                favorites={favorites}
                cycleIdx={cycleIdx}
                onNext={() => setCycleIdx((i) => (i + 1) % favorites.length)}
                onPrev={() => setCycleIdx((i) => (i - 1 + favorites.length) % favorites.length)}
                onStop={() => setCycling(false)}
                onToggleFav={toggleFav}
              />
            ) : (
              <div
                className="grid gap-2 h-full"
                style={{
                  gridTemplateColumns: `repeat(${wallGrid.cols}, minmax(0, 1fr))`,
                  gridTemplateRows: `repeat(${wallGrid.rows}, minmax(0, 1fr))`,
                }}
                data-testid="camera-wall-grid"
              >
                {favorites.map((f) => {
                  const cam = {
                    id: f.camera_id, name: f.name, snapshot_url: f.url,
                    city: f.city, state: f.state, region: f.region || f.city,
                    highway: f.highway || "★",
                    video_url: f.video_url || "",
                    snapshot_alive: f.snapshot_alive !== false,
                  };
                  return (
                    <CameraCard key={cam.id} cam={cam} favored onToggle={() => toggleFav(cam)} sizeMode="wall" />
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="flex-1 overflow-auto p-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {items.map((c) => (
                <CameraCard key={c.id} cam={c} favored={favIds.has(c.id)} onToggle={toggleFav} />
              ))}
              {!items.length && !loading && (
                <div className="col-span-full text-center py-12 text-muted-foreground">No cameras found</div>
              )}
              {loading && (
                <div className="col-span-full text-center py-12 text-muted-foreground font-mono text-xs">LOADING…</div>
              )}
            </div>
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-center gap-3 py-4 font-mono text-xs" data-testid="camera-pagination">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span>Page {page + 1} / {pageCount}</span>
                <Button size="sm" variant="outline" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
